import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { merchantApi } from '../api/config';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faShieldAlt, faSpinner, faCheckCircle, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';

/**
 * หน้าบังคับเปลี่ยนรหัสผ่านเมื่อ login ครั้งแรก
 * แสดงแทนทุกหน้าใต้ Merchant Portal เมื่อ must_change_password = true
 */
const ForceChangePasswordPage: React.FC = () => {
  const { setMustChangePassword, logout } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // ตรวจความแข็งแรงรหัส (ให้ตรงกับ backend: อย่างน้อย 10 ตัว + ตัวเลข + อักขระพิเศษ)
  const hasNumber = /[0-9]/.test(newPassword);
  const hasSpecial = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(newPassword);
  const lengthOk = newPassword.length >= 10;
  const notSameAsOld = newPassword.length > 0 && newPassword !== currentPassword;
  const matchOk = confirmPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = lengthOk && hasNumber && hasSpecial && notSameAsOld && matchOk && currentPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    try {
      await merchantApi.post('/merchants/change-password-forced', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      toast.success('เปลี่ยนรหัสผ่านสำเร็จ');
      setMustChangePassword(false);
      navigate('/', { replace: true });
    } catch (err: any) {
      const msg = err.response?.data?.errors?.[0] || err.response?.data?.message || 'เกิดข้อผิดพลาด';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const Requirement: React.FC<{ ok: boolean; label: string }> = ({ ok, label }) => (
    <li className={`flex items-center gap-2 text-xs ${ok ? 'text-green-400' : 'text-gray-500'}`}>
      <FontAwesomeIcon icon={faCheckCircle} className={ok ? 'text-green-400' : 'text-gray-600'} />
      {label}
    </li>
  );

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    outline: 'none',
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#0a0f1e' }}>
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
               style={{ background: 'var(--theme-primary-light)' }}>
            <FontAwesomeIcon icon={faShieldAlt} className="text-2xl text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">ตั้งรหัสผ่านใหม่</h1>
          <p className="text-gray-400 text-sm">
            เพื่อความปลอดภัย กรุณาเปลี่ยนรหัสผ่านที่ผู้ดูแลระบบตั้งให้ ก่อนเริ่มใช้งาน
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
              รหัสผ่านปัจจุบัน
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoFocus
              autoComplete="current-password"
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 transition-all"
              style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--theme-primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
              รหัสผ่านใหม่
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 transition-all"
              style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--theme-primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
            />
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
              ยืนยันรหัสผ่านใหม่
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="••••••••"
              className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-gray-600 transition-all"
              style={inputStyle}
              onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--theme-primary)')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
            />
          </div>

          {/* Requirements checklist */}
          <ul className="space-y-1.5 p-3 rounded-xl bg-white/5 border border-white/10">
            <Requirement ok={lengthOk} label="อย่างน้อย 10 ตัวอักษร" />
            <Requirement ok={hasNumber} label="มีตัวเลขอย่างน้อย 1 ตัว" />
            <Requirement ok={hasSpecial} label="มีอักขระพิเศษอย่างน้อย 1 ตัว" />
            <Requirement ok={notSameAsOld} label="ต้องไม่ซ้ำกับรหัสผ่านเดิม" />
            <Requirement ok={matchOk} label="ยืนยันรหัสผ่านตรงกัน" />
          </ul>

          <button
            type="submit"
            disabled={loading || !canSubmit}
            className="w-full py-3.5 rounded-xl text-white font-semibold text-sm
                       disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
            style={{ background: 'var(--theme-primary-gradient)' }}
          >
            {loading
              ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />กำลังบันทึก...</>
              : <><FontAwesomeIcon icon={faLock} className="mr-2" />บันทึกรหัสผ่านใหม่</>
            }
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="w-full py-3 rounded-xl border border-white/10 text-gray-400 text-sm
                       hover:bg-white/5 transition-colors flex items-center justify-center gap-2"
          >
            <FontAwesomeIcon icon={faRightFromBracket} />
            ออกจากระบบ
          </button>
        </form>
      </div>
    </div>
  );
};

export default ForceChangePasswordPage;
